// tabs/FacilitatorsTab.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar, Badge, Button, Card, Center, Group, Drawer,
  SimpleGrid, Stack, Text, ThemeIcon, ActionIcon, Tooltip,
} from '@mantine/core';
import {
  IconUserPlus, IconUserCheck, IconTrash, IconRefresh, IconStar, IconStarFilled,
} from '@tabler/icons-react';
import {
  useRegistrationService, useFacilitatorService,
  RegistrationFacilitatorView, FacilitatorModelView,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { Select } from '@/components';

interface FacilitatorsTabProps {
  registrationId: number;
  active: boolean;
  onRegistrationChange?: () => void;
}

const displayName = (f: { fullName?: string | null; displayName?: string | null; firstName?: string | null; surname?: string | null; userId: string }) =>
  [f.firstName, f.surname].filter(Boolean).join(' ').trim()
  || f.displayName?.trim()
  || f.fullName?.trim()
  || f.userId;

const getInitials = (name: string) =>
  name.split(' ').filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || 'F';

// ── Facilitator grid card (assigned to this registration) ─────────────────────
function FacilitatorCard({
  facilitator,
  onRemove,
  removing,
}: {
  facilitator: RegistrationFacilitatorView;
  onRemove: () => void;
  removing: boolean;
}) {
  const name = facilitator.fullName?.trim() || facilitator.userId;

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="center" mb="sm">
        <Avatar src={facilitator.avatar ?? undefined} alt={name} size="xl" radius="md">
          {getInitials(name)}
        </Avatar>
      </Group>

      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {name}
      </Text>

      <Group justify="center" mb="xs">
        {facilitator.isLead && (
          <Badge size="sm" variant="light" color="yellow" leftSection={<IconStarFilled size={12} />}>
            Lead Facilitator
          </Badge>
        )}
      </Group>
      

      <Group justify="center" gap={6} mt="md">
        <Tooltip label="Remove from registration" withArrow>
          <ActionIcon
            variant="light"
            color="red"
            loading={removing}
            onClick={onRemove}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
}

function FacilitatorCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm" align="center">
        <Avatar radius="xl" size={52} color="gray">F</Avatar>
        <Text size="sm" c="dimmed">Loading…</Text>
      </Stack>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FacilitatorsTab({ registrationId, active, onRegistrationChange }: FacilitatorsTabProps) {
  const registrationService = useRegistrationService();
  const Facilitators        = useFacilitatorService();

  const [facilitators, setFacilitators]     = useState<RegistrationFacilitatorView[]>([]);
  const [loadingFacilitators, setLoadingFacilitators] = useState(false);
  const [removingId, setRemovingId]         = useState<string | null>(null);
  const [togglingLeadId, setTogglingLeadId] = useState<string | null>(null);

  // All facilitators in the system — used as the add-candidate pool
  const [allFacilitators, setAllFacilitators] = useState<FacilitatorModelView[]>([]);
  const [loadingAll, setLoadingAll]           = useState(false);
  const [selectedId, setSelectedId]           = useState<string | null>(null);
  const [addingId, setAddingId]               = useState<string | null>(null);

  // Drawer controlling the "add facilitator" flow
  const [drawerOpened, setDrawerOpened] = useState(false);

  const fetchFacilitators = useCallback(async () => {
    if (!registrationId) return;
    setLoadingFacilitators(true);
    try {
      const res: any = await registrationService.getFacilitatorsRaw(registrationId);
      const list: RegistrationFacilitatorView[] = res?.data ?? res ?? [];
      setFacilitators(list);
    } catch (e) {
      console.error('Error fetching facilitators:', e);
      toast.error('Error', 'Could not load facilitators.');
    } finally {
      setLoadingFacilitators(false);
    }
  }, [registrationId, registrationService]);

  const fetchAllFacilitators = useCallback(async () => {
    setLoadingAll(true);
    try {
      setAllFacilitators(await Facilitators.list());
    } catch (e) {
      console.error('Error fetching facilitator list:', e);
      toast.error('Error', 'Could not load facilitator list.');
    } finally {
      setLoadingAll(false);
    }
  }, [Facilitators]);

  useEffect(() => {
    if (active) {
      fetchFacilitators();
      fetchAllFacilitators();
    }
  }, [active, fetchFacilitators, fetchAllFacilitators]);

  const openDrawer = () => {
    setSelectedId(null);
    setDrawerOpened(true);
    // make sure the candidate pool is fresh when opening
    fetchAllFacilitators();
  };

  const closeDrawer = () => {
    setDrawerOpened(false);
    setSelectedId(null);
  };

  const handleAddFacilitator = async (isLead: boolean) => {
    if (!selectedId) return;
    const candidate = allFacilitators.find((c) => c.userId === selectedId);
    if (!candidate) return;

    setAddingId(selectedId);
    try {
      await registrationService.assignFacilitator(registrationId, candidate.userId, isLead);
      await fetchFacilitators();
      onRegistrationChange?.();
      toast.success('Facilitator Added', `${displayName(candidate)} has been added to the registration.`);
      setSelectedId(null);
    } catch (e) {
      console.error('Error adding facilitator:', e);
      toast.error('Error', 'Could not add facilitator. Please try again.');
    } finally {
      setAddingId(null);
    }
  };

  const handleRemoveFacilitator = async (facilitator: RegistrationFacilitatorView) => {
    setRemovingId(facilitator.userId);
    try {
      await registrationService.removeFacilitator(registrationId, facilitator.userId);
      await fetchFacilitators();
      onRegistrationChange?.();
      toast.success('Removed', `${facilitator.fullName ?? facilitator.userId} removed from the registration.`);
    } catch (e) {
      console.error('Error removing facilitator:', e);
      toast.error('Error', 'Could not remove facilitator. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleToggleLead = async (facilitator: RegistrationFacilitatorView) => {
    setTogglingLeadId(facilitator.userId);
    try {
      await registrationService.assignFacilitator(registrationId, facilitator.userId, !facilitator.isLead);
      await fetchFacilitators();
      onRegistrationChange?.();
      toast.success(
        'Updated',
        `${facilitator.fullName ?? facilitator.userId} is ${!facilitator.isLead ? 'now' : 'no longer'} the lead facilitator.`
      );
    } catch (e) {
      console.error('Error updating facilitator:', e);
      toast.error('Error', 'Could not update facilitator. Please try again.');
    } finally {
      setTogglingLeadId(null);
    }
  };

  // Candidates = all facilitators, minus those already assigned
  const candidates = useMemo(() => {
    const assignedIds = new Set(facilitators.map((f) => f.userId));
    return allFacilitators.filter((f) => !assignedIds.has(f.userId));
  }, [allFacilitators, facilitators]);

  // Dropdown data — Mantine Select handles the search/filter internally
  const selectData = useMemo(
    () => candidates.map((c) => ({ value: c.userId, label: displayName(c) })),
    [candidates]
  );

  const selectedCandidate = selectedId ? candidates.find((c) => c.userId === selectedId) : null;

  return (
    <Stack gap="lg">
      {/* ── Facilitators grid ── */}
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm" c="dimmed">
            Facilitators on this registration ({facilitators.length})
          </Text>

          <Group gap={6}>
            <Tooltip label="Refresh facilitators" withArrow>
              <ActionIcon
                variant="subtle"
                onClick={() => { fetchFacilitators(); fetchAllFacilitators(); }}
                loading={loadingFacilitators || loadingAll}
                size="lg"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
            <Button
              size="sm"
              leftSection={<IconUserPlus size={14} />}
              onClick={openDrawer}
            >
              Add Facilitator
            </Button>
          </Group>
        </Group>

        {loadingFacilitators ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <FacilitatorCardSkeleton />
            <FacilitatorCardSkeleton />
            <FacilitatorCardSkeleton />
          </SimpleGrid>
        ) : facilitators.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUserCheck size={30} />
              </ThemeIcon>
              <Text fw={600}>No facilitators yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                Click "Add Facilitator" to assign someone to this registration.
              </Text>
              <Button
                size="sm"
                variant="light"
                leftSection={<IconUserPlus size={14} />}
                onClick={openDrawer}
              >
                Add Facilitator
              </Button>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {facilitators.map((f) => (
              <FacilitatorCard
                key={f.userId}
                facilitator={f}
                removing={removingId === f.userId}
                onRemove={() => handleRemoveFacilitator(f)}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {/* ── Add facilitator drawer ── */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="Add Facilitator"
        position="right"
        padding="lg"
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Search facilitators"
            placeholder={loadingAll ? 'Loading facilitators…' : 'Click to search and select…'}
            data={selectData}
            value={selectedId}
            onChange={setSelectedId}
            searchable
            clearable
            disabled={loadingAll}
            nothingFoundMessage="No matching facilitators"
            leftSection={
              selectedCandidate ? (
                <Avatar src={selectedCandidate.avatar ?? undefined} size={20} radius="xl">
                  {getInitials(displayName(selectedCandidate))}
                </Avatar>
              ) : undefined
            }
            renderOption={({ option, checked }) => {
              const c = candidates.find((x) => x.userId === option.value);
              return (
                <Group gap="sm" wrap="nowrap">
                  <Avatar src={c?.avatar ?? undefined} size={28} radius="xl">
                    {getInitials(option.label)}
                  </Avatar>
                  <Stack gap={0}>
                    <Text size="sm">{option.label}</Text>
                    {c?.email && <Text size="xs" c="dimmed">{c.email}</Text>}
                  </Stack>
                </Group>
              );
            }}
          />

          {selectedCandidate && (
            <Card withBorder radius="md" padding="sm">
              <Group gap="sm" wrap="nowrap">
                <Avatar src={selectedCandidate.avatar ?? undefined} size={40} radius="xl">
                  {getInitials(displayName(selectedCandidate))}
                </Avatar>
                <Stack gap={0}>
                  <Text size="sm" fw={600}>{displayName(selectedCandidate)}</Text>
                  {selectedCandidate.email && (
                    <Text size="xs" c="dimmed">{selectedCandidate.email}</Text>
                  )}
                </Stack>
              </Group>
            </Card>
          )}

          <Group justify="flex-end" gap={6}>
            <Button
              variant="light"
              disabled={!selectedId}
              leftSection={<IconUserPlus size={14} />}
              loading={addingId !== null && addingId === selectedId}
              onClick={() => handleAddFacilitator(false)}
            >
              Add
            </Button>
            <Button
              disabled={!selectedId}
              loading={addingId !== null && addingId === selectedId}
              onClick={() => handleAddFacilitator(true)}
            >
              Add as Lead
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </Stack>
  );
}